import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { 
  LineChart, 
  FileText, 
  Building2, 
  MessageCircle,
  BarChart3,
  Gauge,
  Sparkles 
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { ComingSoonOverlay } from "@/components/ui/coming-soon-overlay"

interface ToolCard {
  title: string
  description: string
  icon: React.ReactNode
  comingSoon?: boolean
  path?: string
  stats?: {
    label: string
    value: string
  }[]
}

const tools: ToolCard[] = [
  {
    title: "Impact Dashboard",
    description: "Tracking of corporate volunteer engagement and impact metrics",
    icon: <Gauge className="h-5 w-5 text-emerald-500" />,
    path: "/live-dashboard",
    stats: [
      { label: "Active Volunteers", value: "1.2k" },
      { label: "Hours Contributed", value: "45k" }
    ]
  },
  {
    title: "AI-Generated Impact Reports",
    description: "Automated, data-driven reports showcasing your organization's impact",
    icon: <FileText className="h-5 w-5 text-cyan-500" />,
    path: "/impact-reports",
    stats: [
      { label: "Reports Generated", value: "250+" },
      { label: "Data Points", value: "10k+" }
    ]
  },
  {
    title: "Corporate Partner Reports",
    description: "Custom reports tailored for sponsors and corporate donors",
    icon: <Building2 className="h-5 w-5 text-indigo-500" />,
    path: "/partner-reports",
    stats: [
      { label: "Partners", value: "50+" },
      { label: "ROI Tracked", value: "95%" }
    ]
  },
  {
    title: "Survey & Feedback Generator",
    description: "Create and analyze feedback from corporates and volunteers",
    icon: <MessageCircle className="h-5 w-5 text-violet-500" />,
    path: "/feedback-tools",
    stats: [
      { label: "Response Rate", value: "85%" },
      { label: "Insights Generated", value: "500+" }
    ]
  },
  {
    title: "Post-Event Analytics",
    description: "Comprehensive data on participation, fundraising, and engagement",
    icon: <BarChart3 className="h-5 w-5 text-fuchsia-500" />,
    path: "/event-analytics",
    stats: [
      { label: "Events Analyzed", value: "100+" },
      { label: "Success Rate", value: "92%" }
    ]
  }
]

export default function ImpactReportsAndAnalyticsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()

  return (
    <div className="h-full w-full">
      {/* Content */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <LineChart className="h-8 w-8 text-emerald-500" />
          <div>
            <h1 className="text-2xl font-bold">Impact Reports & Analytics</h1>
            <p className="text-muted-foreground">
              Track, measure, and showcase your social impact
            </p>
          </div>
        </div>

        <div className="relative">
          {/* Coming Soon Overlay */}
          <ComingSoonOverlay message="Impact Analytics - Coming Soon" />
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => (
              <Card
                key={tool.title}
                className="group relative overflow-hidden border border-border/50 bg-background/50 backdrop-blur-sm transition-all hover:border-emerald-200 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="rounded-full bg-emerald-50 p-2 transition-colors group-hover:bg-emerald-100">
                      {tool.icon}
                    </div>
                    {tool.comingSoon && (
                      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-xl">{tool.title}</CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tool.stats && (
                    <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/50 p-3">
                      {tool.stats.map((stat) => (
                        <div key={stat.label} className="text-center">
                          <div className="text-lg font-semibold">{stat.value}</div>
                          <div className="text-xs text-muted-foreground">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white transition-all hover:from-emerald-600 hover:to-cyan-600"
                    disabled={tool.comingSoon}
                    onClick={() => tool.path && navigate(tool.path)}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {tool.comingSoon ? "Coming Soon" : "View Analytics"}
                  </Button>
                </CardContent>

                {/* Decorative corner gradients */}
                <div className="absolute -right-6 -top-6 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
                <div className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-gradient-to-tr from-cyan-200 to-transparent opacity-50 blur-lg transition-all group-hover:opacity-100" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
